export type AchievementType = 'nivel' | 'insignia' | 'record' | 'ranking' | 'logro';

export type AchievementLocation = 'perfil' | 'cabina' | 'feed' | 'club';

export type AchievementAnimation = 'slideUp' | 'fadeIn' | 'pulseBadge' | 'confetti' | 'glow';

export type AchievementStyle = 'subtle' | 'moderate' | 'celebratory';

export interface AchievementTypeConfig {
  key: AchievementType;
  label: string;
  labelPlural: string;
  verb: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  gradient: string;
  description: string;
  animation: AchievementAnimation;
}

export interface LocationConfig {
  key: AchievementLocation;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
  style: AchievementStyle;
  maxSimultaneous: number;
}

export interface LiveAchievement {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  userLevel: number;
  achievementType: AchievementType;
  title: string;
  subtitle: string;
  description: string;
  value: string;
  previousValue: string | null;
  icon: string;
  achievedAt: string;
  achievedAtRelative: string;
  location: AchievementLocation;
  style: AchievementStyle;
  isRead: boolean;
  isRecent: boolean;
  decayMinutes: number;
  reactions: number;
  comments: number;
  relatedEntity?: string;
  relatedEntityName?: string;
}

export interface AchievementStats {
  totalToday: number;
  activeAchievements: number;
  porNivel: number;
  porInsignia: number;
  porRecord: number;
  porRanking: number;
  porLogro: number;
  masComun: string;
  ubicacionMasActiva: string;
  tiempoPromedioDecay: string;
}

export const achievementTypeConfig: Record<AchievementType, AchievementTypeConfig> = {
  nivel: {
    key: 'nivel',
    label: 'Nuevo Nivel',
    labelPlural: 'Nuevos Niveles',
    verb: 'alcanzó',
    icon: 'ri-arrow-up-circle-line',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
    gradient: 'from-teal-500/20 via-teal-400/10 to-emerald-400/5',
    description: 'Un oyente subió de nivel. Cada nivel es experiencia ganada y comunidad reconocida.',
    animation: 'slideUp',
  },
  insignia: {
    key: 'insignia',
    label: 'Nueva Insignia',
    labelPlural: 'Nuevas Insignias',
    verb: 'obtuvo',
    icon: 'ri-award-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    gradient: 'from-amber-500/20 via-amber-400/10 to-yellow-400/5',
    description: 'Una insignia fue desbloqueada. Las insignias marcan hitos, logros y estatus en RFW.',
    animation: 'pulseBadge',
  },
  record: {
    key: 'record',
    label: 'Nuevo Récord',
    labelPlural: 'Nuevos Récords',
    verb: 'rompió',
    icon: 'ri-fire-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    gradient: 'from-rose-500/20 via-rose-400/10 to-pink-400/5',
    description: 'Un récord fue superado. Racha de actividad, mensajes enviados, reacciones — todo cuenta.',
    animation: 'confetti',
  },
  ranking: {
    key: 'ranking',
    label: 'Nuevo Ranking',
    labelPlural: 'Nuevos Rankings',
    verb: 'entró al',
    icon: 'ri-trophy-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    gradient: 'from-violet-500/20 via-violet-400/10 to-purple-400/5',
    description: 'Un oyente entró a un ranking. Posiciones en tops semanales, mensuales o globales.',
    animation: 'fadeIn',
  },
  logro: {
    key: 'logro',
    label: 'Nuevo Logro',
    labelPlural: 'Nuevos Logros',
    verb: 'desbloqueó',
    icon: 'ri-star-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    gradient: 'from-sky-500/20 via-sky-400/10 to-blue-400/5',
    description: 'Un logro fue desbloqueado. Misiones completadas, metas alcanzadas, desafíos superados.',
    animation: 'glow',
  },
};

export const locationConfig: Record<AchievementLocation, LocationConfig> = {
  perfil: {
    key: 'perfil',
    label: 'Perfil',
    icon: 'ri-user-line',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    description: 'Logros visibles en el perfil del oyente. Permanentes, construyen reputación.',
    style: 'subtle',
    maxSimultaneous: 3,
  },
  cabina: {
    key: 'cabina',
    label: 'Cabina',
    icon: 'ri-message-2-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'Logros que aparecen en el chat en vivo. Efímeros, animados, motivan al instante.',
    style: 'celebratory',
    maxSimultaneous: 5,
  },
  feed: {
    key: 'feed',
    label: 'Feed',
    icon: 'ri-layout-masonry-line',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    description: 'Logros en el feed social. Visibles por la comunidad, generan reacciones y comentarios.',
    style: 'moderate',
    maxSimultaneous: 4,
  },
  club: {
    key: 'club',
    label: 'Club',
    icon: 'ri-group-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    description: 'Logros dentro de clubes de fans. Relevantes para la comunidad del club.',
    style: 'moderate',
    maxSimultaneous: 3,
  },
};

export const achievementStats: AchievementStats = {
  totalToday: 1847,
  activeAchievements: 342,
  porNivel: 620,
  porInsignia: 415,
  porRecord: 287,
  porRanking: 318,
  porLogro: 207,
  masComun: 'Nuevo Nivel',
  ubicacionMasActiva: 'Cabina',
  tiempoPromedioDecay: '3.2 min',
};

export const liveAchievements: LiveAchievement[] = [
  // ═══ CABINA (celebratory, efímeros) ═══
  {
    id: 'ach-001',
    userId: 'user-042',
    userName: 'Mariana López',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20mid%20twenties%20bright%20warm%20smile%20professional%20headshot%20natural%20lighting%20clean%20background%20friendly%20approachable%20portrait&width=120&height=120&seq=ach-user-mariana&orientation=squarish',
    userLevel: 28,
    achievementType: 'nivel',
    title: 'Nivel 28 alcanzado',
    subtitle: 'Oyente Experta',
    description: 'Mariana alcanzó el nivel 28 tras 6 meses de actividad diaria en RFW. Su constancia la lleva al top 5% de oyentes más activos.',
    value: 'Nivel 28',
    previousValue: 'Nivel 27',
    icon: 'ri-arrow-up-circle-line',
    achievedAt: '2026-06-21T10:32:00Z',
    achievedAtRelative: 'Hace 2 min',
    location: 'cabina',
    style: 'celebratory',
    isRead: false,
    isRecent: true,
    decayMinutes: 5,
    reactions: 47,
    comments: 12,
    relatedEntity: 'radio',
    relatedEntityName: 'La Mega 107.3 FM',
  },
  {
    id: 'ach-002',
    userId: 'user-087',
    userName: 'Carlos Vega',
    userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20male%20late%20twenties%20warm%20smile%20casual%20style%20professional%20headshot%20clean%20background%20friendly%20approachable%20personality%20portrait&width=120&height=120&seq=ach-user-carlosv&orientation=squarish',
    userLevel: 35,
    achievementType: 'insignia',
    title: 'Insignia Top Fan Diamante',
    subtitle: 'La insignia más alta de fan',
    description: 'Carlos obtuvo la insignia Top Fan Diamante tras 12 meses consecutivos como Top 10 Fan de La Mega. Un logro que solo 50 oyentes en toda RFW tienen.',
    value: 'Top Fan Diamante',
    previousValue: 'Top Fan Platino',
    icon: 'ri-vip-diamond-line',
    achievedAt: '2026-06-21T10:28:00Z',
    achievedAtRelative: 'Hace 5 min',
    location: 'cabina',
    style: 'celebratory',
    isRead: false,
    isRecent: true,
    decayMinutes: 8,
    reactions: 89,
    comments: 24,
    relatedEntity: 'radio',
    relatedEntityName: 'La Mega 107.3 FM',
  },
  {
    id: 'ach-003',
    userId: 'user-156',
    userName: 'Andrea Ruiz',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20woman%20mid%20twenties%20confident%20warm%20smile%20studio%20portrait%20clean%20background%20modern%20friendly%20approachable&width=120&height=120&seq=ach-user-andrea&orientation=squarish',
    userLevel: 19,
    achievementType: 'record',
    title: 'Récord: 500 mensajes en un día',
    subtitle: 'La oyente más activa hoy',
    description: 'Andrea rompió el récord de la cabina con 500 mensajes en un solo día. La cabina de Onda Cero nunca había visto tanta participación.',
    value: '500 mensajes',
    previousValue: '387 mensajes',
    icon: 'ri-fire-line',
    achievedAt: '2026-06-21T10:15:00Z',
    achievedAtRelative: 'Hace 18 min',
    location: 'cabina',
    style: 'celebratory',
    isRead: true,
    isRecent: true,
    decayMinutes: 15,
    reactions: 134,
    comments: 41,
    relatedEntity: 'radio',
    relatedEntityName: 'Onda Cero 104.5 FM',
  },
  {
    id: 'ach-004',
    userId: 'user-203',
    userName: 'Diego Martínez',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latino%20male%20early%20twenties%20urban%20style%20friendly%20smile%20professional%20headshot%20clean%20background%20approachable%20personality%20portrait&width=120&height=120&seq=ach-user-diegom&orientation=squarish',
    userLevel: 22,
    achievementType: 'ranking',
    title: '#3 en Top Oyentes Semanal',
    subtitle: 'Ranking Semanal RFW',
    description: 'Diego entró al Top 3 del ranking semanal de oyentes más activos de RFW. Su participación en 8 radios distintas lo impulsó al podio.',
    value: 'Top 3 Semanal',
    previousValue: 'Top 12 Semanal',
    icon: 'ri-medal-line',
    achievedAt: '2026-06-21T10:05:00Z',
    achievedAtRelative: 'Hace 28 min',
    location: 'cabina',
    style: 'celebratory',
    isRead: true,
    isRecent: true,
    decayMinutes: 20,
    reactions: 72,
    comments: 18,
    relatedEntity: 'ranking',
    relatedEntityName: 'Top Oyentes Semanal',
  },
  {
    id: 'ach-005',
    userId: 'user-078',
    userName: 'Sofía Herrera',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20female%20early%20thirties%20warm%20engaging%20smile%20studio%20headshot%20clean%20background%20natural%20lighting%20friendly&width=120&height=120&seq=ach-user-sofiah&orientation=squarish',
    userLevel: 41,
    achievementType: 'logro',
    title: 'Logro: Oyente de Oro',
    subtitle: '1,000 horas de escucha',
    description: 'Sofía desbloqueó el logro Oyente de Oro tras acumular 1,000 horas de escucha en RFW. Una dedicación que la pone en el 1% de los oyentes más fieles.',
    value: 'Oyente de Oro',
    previousValue: null,
    icon: 'ri-star-fill',
    achievedAt: '2026-06-21T09:52:00Z',
    achievedAtRelative: 'Hace 41 min',
    location: 'cabina',
    style: 'celebratory',
    isRead: true,
    isRecent: true,
    decayMinutes: 30,
    reactions: 56,
    comments: 14,
    relatedEntity: 'logro',
    relatedEntityName: 'Oyente de Oro',
  },

  // ═══ FEED (moderate, social) ═══
  {
    id: 'ach-006',
    userId: 'user-112',
    userName: 'Fernando Gutiérrez',
    userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20male%20mid%20thirties%20professional%20warm%20smile%20clean%20headshot%20studio%20portrait%20natural%20lighting%20friendly%20approachable&width=120&height=120&seq=ach-user-fernando&orientation=squarish',
    userLevel: 33,
    achievementType: 'nivel',
    title: 'Nivel 33 alcanzado',
    subtitle: 'Oyente Veterano',
    description: 'Fernando subió al nivel 33 desbloqueando acceso a beneficios exclusivos y mayor visibilidad en la comunidad RFW.',
    value: 'Nivel 33',
    previousValue: 'Nivel 32',
    icon: 'ri-arrow-up-circle-line',
    achievedAt: '2026-06-21T09:30:00Z',
    achievedAtRelative: 'Hace 1 hora',
    location: 'feed',
    style: 'moderate',
    isRead: false,
    isRecent: true,
    decayMinutes: 60,
    reactions: 28,
    comments: 7,
    relatedEntity: 'perfil',
    relatedEntityName: 'Perfil de Fernando',
  },
  {
    id: 'ach-007',
    userId: 'user-189',
    userName: 'Valeria Córdoba',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20mid%20twenties%20fashion%20forward%20warm%20smile%20professional%20headshot%20studio%20lighting%20clean%20background%20confident&width=120&height=120&seq=ach-user-valeria&orientation=squarish',
    userLevel: 15,
    achievementType: 'insignia',
    title: 'Insignia Creadora de Contenido',
    subtitle: '50 posts publicados',
    description: 'Valeria recibió la insignia Creadora de Contenido por publicar 50 posts con más de 100 reacciones cada uno. Su contenido conecta con la comunidad.',
    value: 'Creadora de Contenido',
    previousValue: null,
    icon: 'ri-quill-pen-line',
    achievedAt: '2026-06-21T08:45:00Z',
    achievedAtRelative: 'Hace 2 horas',
    location: 'feed',
    style: 'moderate',
    isRead: false,
    isRecent: true,
    decayMinutes: 90,
    reactions: 95,
    comments: 31,
    relatedEntity: 'feed',
    relatedEntityName: 'Feed Social RFW',
  },
  {
    id: 'ach-008',
    userId: 'user-045',
    userName: 'Mateo Restrepo',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20mid%20twenties%20modern%20style%20friendly%20expression%20professional%20headshot%20clean%20background%20approachable%20warm%20smile&width=120&height=120&seq=ach-user-mateo&orientation=squarish',
    userLevel: 26,
    achievementType: 'record',
    title: 'Récord: 30 días de racha',
    subtitle: 'Racha de actividad diaria',
    description: 'Mateo alcanzó 30 días consecutivos de actividad en RFW. La racha más larga de este mes. Su dedicación diaria lo convierte en un pilar de la comunidad.',
    value: '30 días de racha',
    previousValue: '28 días de racha',
    icon: 'ri-fire-line',
    achievedAt: '2026-06-21T08:20:00Z',
    achievedAtRelative: 'Hace 2 horas',
    location: 'feed',
    style: 'moderate',
    isRead: true,
    isRecent: true,
    decayMinutes: 120,
    reactions: 142,
    comments: 38,
    relatedEntity: 'perfil',
    relatedEntityName: 'Racha de Mateo',
  },
  {
    id: 'ach-009',
    userId: 'user-267',
    userName: 'Lucía Fernández',
    userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20late%20twenties%20elegant%20warm%20smile%20studio%20portrait%20clean%20background%20natural%20lighting%20confident%20friendly&width=120&height=120&seq=ach-user-lucia&orientation=squarish',
    userLevel: 38,
    achievementType: 'ranking',
    title: '#1 en Top Fans Rock & Pop',
    subtitle: 'Ranking de la Radio',
    description: 'Lucía es la fan #1 de Rock & Pop 95.1 FM este mes. Sus 2,847 interacciones la coronan como la oyente más apasionada del rock en RFW.',
    value: 'Fan #1 Rock & Pop',
    previousValue: 'Fan #4 Rock & Pop',
    icon: 'ri-medal-fill',
    achievedAt: '2026-06-21T07:55:00Z',
    achievedAtRelative: 'Hace 3 horas',
    location: 'feed',
    style: 'moderate',
    isRead: true,
    isRecent: true,
    decayMinutes: 180,
    reactions: 203,
    comments: 52,
    relatedEntity: 'radio',
    relatedEntityName: 'Rock & Pop 95.1 FM',
  },
  {
    id: 'ach-010',
    userId: 'user-134',
    userName: 'Tomás Alarcón',
    userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20friendly%20warm%20smile%20professional%20headshot%20studio%20lighting%20clean%20background%20natural%20approachable&width=120&height=120&seq=ach-user-tomas&orientation=squarish',
    userLevel: 17,
    achievementType: 'logro',
    title: 'Logro: Coleccionista',
    subtitle: '10 insignias coleccionadas',
    description: 'Tomás desbloqueó el logro Coleccionista tras obtener 10 insignias distintas en RFW. De novato a coleccionista en solo 4 meses.',
    value: 'Coleccionista',
    previousValue: null,
    icon: 'ri-archive-line',
    achievedAt: '2026-06-21T07:30:00Z',
    achievedAtRelative: 'Hace 3 horas',
    location: 'feed',
    style: 'moderate',
    isRead: true,
    isRecent: false,
    decayMinutes: 240,
    reactions: 67,
    comments: 19,
    relatedEntity: 'insignias',
    relatedEntityName: 'Colección de Insignias',
  },

  // ═══ PERFIL (subtle, permanentes) ═══
  {
    id: 'ach-011',
    userId: 'user-022',
    userName: 'Gabriela Torres',
    userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20early%20thirties%20elegant%20confident%20warm%20smile%20studio%20portrait%20clean%20background%20sophisticated%20approachable&width=120&height=120&seq=ach-user-gabriela&orientation=squarish',
    userLevel: 45,
    achievementType: 'nivel',
    title: 'Nivel 45 alcanzado',
    subtitle: 'Oyente Elite',
    description: 'Gabriela llegó al nivel 45 entrando al exclusivo grupo de Oyentes Elite. Solo el 0.5% de los oyentes de RFW alcanzan este nivel.',
    value: 'Nivel 45',
    previousValue: 'Nivel 44',
    icon: 'ri-arrow-up-circle-fill',
    achievedAt: '2026-06-20T18:40:00Z',
    achievedAtRelative: 'Ayer',
    location: 'perfil',
    style: 'subtle',
    isRead: true,
    isRecent: false,
    decayMinutes: 1440,
    reactions: 15,
    comments: 4,
    relatedEntity: 'perfil',
    relatedEntityName: 'Perfil de Gabriela',
  },
  {
    id: 'ach-012',
    userId: 'user-056',
    userName: 'Julián Ríos',
    userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20thirties%20professional%20friendly%20smile%20studio%20headshot%20clean%20background%20natural%20lighting%20warm%20personality&width=120&height=120&seq=ach-user-julian&orientation=squarish',
    userLevel: 31,
    achievementType: 'insignia',
    title: 'Insignia Moderador Destacado',
    subtitle: 'Reconocimiento de la comunidad',
    description: 'Julián recibió la insignia Moderador Destacado por su impecable labor en 3 clubes. La comunidad votó y él fue elegido como el mejor moderador del trimestre.',
    value: 'Moderador Destacado',
    previousValue: null,
    icon: 'ri-shield-star-line',
    achievedAt: '2026-06-20T14:20:00Z',
    achievedAtRelative: 'Ayer',
    location: 'perfil',
    style: 'subtle',
    isRead: true,
    isRecent: false,
    decayMinutes: 1440,
    reactions: 42,
    comments: 11,
    relatedEntity: 'clubes',
    relatedEntityName: 'Clubes RFW',
  },
  {
    id: 'ach-013',
    userId: 'user-198',
    userName: 'Natalia Paredes',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20female%20mid%20twenties%20bright%20smile%20warm%20friendly%20studio%20portrait%20clean%20background%20natural%20confident&width=120&height=120&seq=ach-user-natalia&orientation=squarish',
    userLevel: 20,
    achievementType: 'logro',
    title: 'Logro: Madrugadora',
    subtitle: '100 saludos antes de las 6 AM',
    description: 'Natalia desbloqueó el logro Madrugadora tras enviar 100 saludos en cabinas antes de las 6 AM. La oyente más madrugadora de RFW este año.',
    value: 'Madrugadora',
    previousValue: null,
    icon: 'ri-sun-line',
    achievedAt: '2026-06-20T05:55:00Z',
    achievedAtRelative: 'Ayer',
    location: 'perfil',
    style: 'subtle',
    isRead: true,
    isRecent: false,
    decayMinutes: 1440,
    reactions: 23,
    comments: 6,
    relatedEntity: 'perfil',
    relatedEntityName: 'Perfil de Natalia',
  },
  {
    id: 'ach-014',
    userId: 'user-310',
    userName: 'Esteban Duque',
    userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20forties%20mature%20friendly%20smile%20professional%20headshot%20studio%20portrait%20clean%20background%20warm%20approachable&width=120&height=120&seq=ach-user-esteban&orientation=squarish',
    userLevel: 52,
    achievementType: 'record',
    title: 'Récord: 10,000 reacciones',
    subtitle: 'Total histórico de reacciones',
    description: 'Esteban alcanzó 10,000 reacciones acumuladas en RFW. Un récord que solo 8 oyentes han logrado en toda la historia de la plataforma.',
    value: '10,000 reacciones',
    previousValue: '9,874 reacciones',
    icon: 'ri-heart-pulse-line',
    achievedAt: '2026-06-19T22:10:00Z',
    achievedAtRelative: 'Anteayer',
    location: 'perfil',
    style: 'subtle',
    isRead: true,
    isRecent: false,
    decayMinutes: 2880,
    reactions: 58,
    comments: 15,
    relatedEntity: 'perfil',
    relatedEntityName: 'Perfil de Esteban',
  },
  {
    id: 'ach-015',
    userId: 'user-074',
    userName: 'Camila Rojas',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20female%20late%20twenties%20stylish%20warm%20smile%20professional%20headshot%20studio%20lighting%20clean%20background%20confident%20approachable&width=120&height=120&seq=ach-user-camila&orientation=squarish',
    userLevel: 24,
    achievementType: 'ranking',
    title: 'Top 5 Ganadoras de Concursos',
    subtitle: 'Ranking de Ganadores',
    description: 'Camila entró al Top 5 del ranking de mayores ganadoras de concursos en RFW. Con 8 concursos ganados, es una competidora imparable.',
    value: 'Top 5 Ganadoras',
    previousValue: 'Top 8 Ganadoras',
    icon: 'ri-trophy-fill',
    achievedAt: '2026-06-19T16:30:00Z',
    achievedAtRelative: 'Anteayer',
    location: 'perfil',
    style: 'subtle',
    isRead: true,
    isRecent: false,
    decayMinutes: 2880,
    reactions: 34,
    comments: 9,
    relatedEntity: 'concursos',
    relatedEntityName: 'Concursos RFW',
  },

  // ═══ CLUB (moderate, grupal) ═══
  {
    id: 'ach-016',
    userId: 'user-088',
    userName: 'Renata Silva',
    userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20early%20thirties%20warm%20engaging%20smile%20studio%20headshot%20clean%20background%20natural%20lighting%20confident%20friendly&width=120&height=120&seq=ach-user-renata&orientation=squarish',
    userLevel: 29,
    achievementType: 'nivel',
    title: 'Nivel 29 alcanzado en el Club',
    subtitle: 'Club Fans La Mega',
    description: 'Renata subió al nivel 29 dentro del Club Fans La Mega. Su participación activa en los retos del club la catapultó 3 niveles en una semana.',
    value: 'Nivel 29 (Club)',
    previousValue: 'Nivel 26 (Club)',
    icon: 'ri-arrow-up-circle-line',
    achievedAt: '2026-06-21T09:15:00Z',
    achievedAtRelative: 'Hace 1 hora',
    location: 'club',
    style: 'moderate',
    isRead: false,
    isRecent: true,
    decayMinutes: 90,
    reactions: 18,
    comments: 5,
    relatedEntity: 'club',
    relatedEntityName: 'Club Fans La Mega',
  },
  {
    id: 'ach-017',
    userId: 'user-153',
    userName: 'Felipe Navarro',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20mid%20twenties%20friendly%20casual%20style%20warm%20smile%20professional%20headshot%20clean%20background%20approachable%20personality&width=120&height=120&seq=ach-user-felipen&orientation=squarish',
    userLevel: 16,
    achievementType: 'insignia',
    title: 'Insignia Retador del Mes',
    subtitle: 'Legión del Rock',
    description: 'Felipe ganó la insignia Retador del Mes en Legión del Rock por completar los 5 retos de junio antes que nadie.',
    value: 'Retador del Mes',
    previousValue: null,
    icon: 'ri-sword-line',
    achievedAt: '2026-06-21T08:50:00Z',
    achievedAtRelative: 'Hace 2 horas',
    location: 'club',
    style: 'moderate',
    isRead: false,
    isRecent: true,
    decayMinutes: 90,
    reactions: 27,
    comments: 8,
    relatedEntity: 'club',
    relatedEntityName: 'Legión del Rock',
  },
  {
    id: 'ach-018',
    userId: 'user-241',
    userName: 'Adriana Mora',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20late%20twenties%20elegant%20warm%20expression%20studio%20portrait%20clean%20background%20natural%20lighting%20confident%20professional&width=120&height=120&seq=ach-user-adriana&orientation=squarish',
    userLevel: 37,
    achievementType: 'record',
    title: 'Récord del Club: Más retos completados',
    subtitle: 'Comunidad Deportiva RFW',
    description: 'Adriana rompió el récord del club completando 15 retos en un mes. La participante más dedicada que la Comunidad Deportiva haya visto.',
    value: '15 retos en 1 mes',
    previousValue: '12 retos en 1 mes',
    icon: 'ri-fire-line',
    achievedAt: '2026-06-20T20:15:00Z',
    achievedAtRelative: 'Ayer',
    location: 'club',
    style: 'moderate',
    isRead: true,
    isRecent: false,
    decayMinutes: 180,
    reactions: 41,
    comments: 13,
    relatedEntity: 'club',
    relatedEntityName: 'Comunidad Deportiva RFW',
  },
  {
    id: 'ach-019',
    userId: 'user-295',
    userName: 'Verónica Campos',
    userPhoto: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20female%20late%20twenties%20bright%20warm%20smile%20studio%20headshot%20clean%20background%20natural%20lighting%20friendly%20approachable&width=120&height=120&seq=ach-user-veronica&orientation=squarish',
    userLevel: 14,
    achievementType: 'logro',
    title: 'Logro: Integradora del Club',
    subtitle: 'Tropical Lovers',
    description: 'Verónica desbloqueó el logro Integradora del Club por invitar a 20 nuevos miembros a Tropical Lovers en un solo mes. La comunidad crece gracias a ella.',
    value: 'Integradora del Club',
    previousValue: null,
    icon: 'ri-user-heart-line',
    achievedAt: '2026-06-20T15:40:00Z',
    achievedAtRelative: 'Ayer',
    location: 'club',
    style: 'moderate',
    isRead: true,
    isRecent: false,
    decayMinutes: 180,
    reactions: 35,
    comments: 10,
    relatedEntity: 'club',
    relatedEntityName: 'Tropical Lovers',
  },
  {
    id: 'ach-020',
    userId: 'user-176',
    userName: 'Leonardo Paz',
    userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20professional%20confident%20smile%20studio%20headshot%20clean%20background%20warm%20natural%20lighting%20friendly%20personality&width=120&height=120&seq=ach-user-leonardo&orientation=squarish',
    userLevel: 40,
    achievementType: 'ranking',
    title: '#2 en Ranking del Club',
    subtitle: 'Club de Oyentes Top',
    description: 'Leonardo escaló al puesto #2 en el ranking interno del Club de Oyentes Top. Solo 5 puntos lo separan del líder.',
    value: 'Ranking Club #2',
    previousValue: 'Ranking Club #7',
    icon: 'ri-medal-line',
    achievedAt: '2026-06-20T11:10:00Z',
    achievedAtRelative: 'Ayer',
    location: 'club',
    style: 'moderate',
    isRead: true,
    isRecent: false,
    decayMinutes: 240,
    reactions: 22,
    comments: 6,
    relatedEntity: 'club',
    relatedEntityName: 'Club de Oyentes Top',
  },
];

export const achievementRules = [
  {
    id: 1,
    title: 'Celebrar sin ser invasivo',
    description: 'Los logros se muestran con animaciones sutiles que no interrumpen la experiencia. En cabina son efímeros (5-30 min), en perfil son permanentes pero discretos. Cada ubicación tiene su propio nivel de intensidad visual para no saturar.',
    icon: 'ri-sparkling-line',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
  },
  {
    id: 2,
    title: 'Momento justo, lugar justo',
    description: 'Cada achievement aparece en el contexto correcto: los récords de cabina en la cabina, los logros de club en el club, los niveles en el perfil. Nunca fuera de contexto. La relevancia es tan importante como el logro mismo.',
    icon: 'ri-focus-3-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    id: 3,
    title: 'Efímero donde molesta, permanente donde suma',
    description: 'En espacios de alta interacción (cabina, feed) los achievements desaparecen naturalmente. En el perfil quedan como historial de logros. Lo que suma al perfil se queda; lo que distrae en el chat se va.',
    icon: 'ri-hourglass-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
];

export function getAchievementsByType(type: AchievementType | 'todas'): LiveAchievement[] {
  if (type === 'todas') return liveAchievements;
  return liveAchievements.filter((a) => a.achievementType === type);
}

export function getAchievementsByLocation(location: AchievementLocation | 'todas'): LiveAchievement[] {
  if (location === 'todas') return liveAchievements;
  return liveAchievements.filter((a) => a.location === location);
}

export function getRecentAchievements(): LiveAchievement[] {
  return liveAchievements.filter((a) => a.isRecent);
}

export function getActiveAchievements(): LiveAchievement[] {
  return liveAchievements.filter((a) => !a.isRead || a.isRecent);
}

export function getAchievementsByStyle(style: AchievementStyle | 'todas'): LiveAchievement[] {
  if (style === 'todas') return liveAchievements;
  return liveAchievements.filter((a) => a.style === style);
}